import { useState, useEffect, useCallback } from 'react';
import { orderBy } from '@progress/kendo-data-query';
import debounce from 'lodash/debounce';
// consts
import { requestStatuses } from 'shared/../constants/store';
import { gridDataHandlingStrategies } from 'shared/../constants/gridDataHandlingStrategies';

const defaultRemapFields = (item) => item;
const defaultSelected = [];

const compareByDirection = (value1, value2, dir) => {
  // Check for null or undefined values first
  if (value1 == null && value2 == null) {
    return 0;
  }
  if (value1 == null) {
    return dir === 'asc' ? 1 : -1;
  }
  if (value2 == null) {
    return dir === 'asc' ? -1 : 1;
  }

  // Compare number types
  if (typeof value1 === 'number' && typeof value2 === 'number') {
    return dir === 'asc' ? value1 - value2 : value2 - value1;
  }

  // Compare string types
  if (typeof value1 === 'string' && typeof value2 === 'string') {
    return dir === 'asc' ? value1.localeCompare(value2) : value2.localeCompare(value1);
  }

  // Compare boolean types
  if (typeof value1 === 'boolean' && typeof value2 === 'boolean') {
    return dir === 'asc' ? Number(value1) - Number(value2) : Number(value2) - Number(value1);
  }

  // Compare date types
  if (value1 instanceof Date && value2 instanceof Date) {
    return dir === 'asc' ? value1 - value2 : value2 - value1;
  }

  // Default comparison as strings
  return dir === 'asc' ? String(value1).localeCompare(String(value2)) : String(value2).localeCompare(String(value1));
};

const defaultParams = {};
const defaultSort = [];

/**
 * @param {Object=>Object} remapFields - Map grid fields incoming so that export and sort will provide correct api values
 */
const useGridData = ({
  getData,
  searchParams,
  isLocalData = true,
  reloadOnSort = false,
  clientSideSorting = false,
  // Sort
  sort = defaultSort, // Telerik format
  sortParams = defaultParams, // Params for requests
  // Pagination
  page = defaultParams, // Telerik format
  pageParams = defaultParams, // Params for requests
  onPageChange,
  // Selected items
  onlyShowSelected = false,
  selected = defaultSelected,
  // Other
  dataHandlingStrategy = gridDataHandlingStrategies.DEFAULT,
  process = null,
  dataState = null,
  ready = true,
  remapFields = defaultRemapFields,
  onFetchError,
}) => {
  // Loading status
  const [status, setStatus] = useState(requestStatuses.INIT);
  const [error, setError] = useState(null);

  // If data is loading on the background (sort, page or reloadOnBackground)
  const [isBgLoading, setIsBgLoading] = useState(false);

  // Grid data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [subTotal, setSubTotal] = useState(0);

  const gridDataStrategiesHandlers = {
    [gridDataHandlingStrategies.DEFAULT]: ({ data, total, subTotal }) => {
      const newData = process ? process(data, dataState).data : data;
      setItems(newData);
      setTotal(total ?? data.length);
      setSubTotal(subTotal);
    },
    [gridDataHandlingStrategies.APPEND]: ({ data, total }) => {
      setItems((prev) => {
        let dataToAdd = [...prev, ...data];
        if (prev.every((x) => x.items && x.groupId && x.field)) {
          const itemsArrays = prev.map((x) => x.items);
          dataToAdd = [...[].concat(...itemsArrays), ...data];
        }
        const newData = process ? process(dataToAdd, dataState).data : data;
        return newData;
      });
      setTotal((prev) => prev + (total ?? data.length));
    },
  };

  // Grid data loader
  const getDataCallback = useCallback(
    debounce(async (params, dataHandlingStrategy, handleState = true) => {
      if (!ready) {
        return;
      }
      if (handleState) {
        setStatus(requestStatuses.PENDING);
      }

      try {
        const { data, total, subTotal } = await getData(params);
        // If page is empty - load prev one if one exists
        if (total > 0 && data.length === 0 && params.page > 1 && (!isLocalData || reloadOnSort) && onPageChange) {
          onPageChange({ page: { ...page, skip: page.skip - page.take } });
          return;
        }

        const dataHandler = gridDataStrategiesHandlers[dataHandlingStrategy];
        // this may need to be consollidated w/ process, but it wasn't documented well enough for me to tell
        const mappedData = { data: data.map(remapFields), total, subTotal };
        dataHandler(mappedData);
        setStatus(requestStatuses.SUCCESS);
      } catch (err) {
        setError(err);
        setStatus(requestStatuses.FAILED);
        onFetchError?.(err);
      } finally {
        setIsBgLoading(false);
      }
    }, 500),
    [ready, page, getData, onFetchError],
  );

  // Data reload handler
  const reload = (passedParams) => {
    if (!ready) {
      return;
    }
    setError(null);

    let queryParams = { ...searchParams, ...passedParams };

    if (!isLocalData || reloadOnSort) {
      queryParams = { ...queryParams, ...pageParams, ...sortParams };
    }

    getDataCallback(queryParams, dataHandlingStrategy);
  };

  // Reloading in background
  const reloadOnBackground = (passedParams) => {
    if (!ready) {
      return;
    }
    setIsBgLoading(true);
    let queryParams = { ...searchParams, ...passedParams };

    if (!isLocalData || reloadOnSort) {
      queryParams = { ...queryParams, ...pageParams, ...sortParams };
    }

    getDataCallback(queryParams, dataHandlingStrategy, false);
  };

  // Initial loading
  useEffect(() => {
    if (!ready) {
      return;
    }
    reload();
  }, [searchParams, onlyShowSelected, ready]);

  // Reload on sorting/pagination change
  useEffect(() => {
    if (status !== requestStatuses.SUCCESS || !reloadOnSort || isLocalData || !ready) {
      return;
    }
    reloadOnBackground();
  }, [sortParams]);

  useEffect(() => {
    if (isLocalData || !ready) {
      return;
    }
    reloadOnBackground();
  }, [pageParams]);

  // Sorting on client side
  const sortDataOnClientSide = (data) => {
    if (data.length === 0) {
      return data;
    }
    return data.slice().sort((a, b) => sort.reduce((acc, sortDescriptor) => {
      const { field, dir } = sortDescriptor;
      const value1 = a[field];
      const value2 = b[field];

      return compareByDirection(value1, value2, dir);
    }, 0));
  };

  // Grid data to be shown on the grid
  const structuredItems = () => {
    // eslint-disable-next-line no-nested-ternary
    const paginated = reloadOnSort && !isLocalData
      ? items : items.length > page.take
        ? orderBy(items, sort).slice(page.skip, page.take + page.skip)
        : orderBy(items, sort);
    const selectedIds = selected.map((x) => x.id);
    const selectedItems = onlyShowSelected ? paginated.filter((x) => selectedIds.includes(x.id)) : paginated;
    return clientSideSorting ? sortDataOnClientSide(selectedItems) : selectedItems;
  };

  const structured = structuredItems();
  const hasData = structured?.length > 0;

  return {
    status,
    items: structured,
    hasData,
    total,
    subTotal,
    error,
    setItems,
    reload,
    reloadOnBackground,
    isBgLoading,
  };
};

export default useGridData;
